import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import IcfLayout, { ScreenHeader, StepCta } from "./IcfLayout";
import StoryNarratorBar from "./story/StoryNarratorBar";
import {
    ChevronDown,
    ChevronRight,
    CheckCircle2,
    AlertTriangle,
    AlertOctagon,
    Info,
    X,
    ChevronLeft,
    ChevronRight as ChR,
    Filter,
} from "lucide-react";
import {
    ICF_EXCERPT,
    RULE_STATUS,
    FINDINGS,
    FINDINGS_SUMMARY,
    PARTICIPANT_SUMMARY,
    PARTICIPANTS,
    P0003_DETAIL,
} from "../../data/icfSeed";

// ---------- shared pills ----------

const STATUS_STYLES = {
    Satisfied: { color: "#166534", bg: "#DCFCE7", bd: "#86EFAC", icon: CheckCircle2 },
    Gap: { color: "#9A3412", bg: "#FFEDD5", bd: "#FED7AA", icon: AlertTriangle },
    "Critical Gap": { color: "#991B1B", bg: "#FEE2E2", bd: "#FCA5A5", icon: AlertOctagon },
    Advisory: { color: "#854D0E", bg: "#FEF3C7", bd: "#FDE68A", icon: Info },
};

const StatusPill = ({ status, small }) => {
    const s = STATUS_STYLES[status];
    if (!s) return null;
    const Icon = s.icon;
    return (
        <span
            className={`mono uppercase tracking-wider border rounded-sm inline-flex items-center gap-1 ${
                small ? "text-[9px] px-1.5 py-[1px]" : "text-[10px] px-2 py-[2px]"
            }`}
            style={{ color: s.color, background: s.bg, borderColor: s.bd }}
        >
            <Icon size={small ? 9 : 10} />
            {status}
        </span>
    );
};

// ---------- Tab A: Document Compliance ----------

const IcfExcerptPanel = () => (
    <div className="border border-hair bg-white p-5" data-testid="tab-a-icf">
        <div className="overline mb-3">Current Master ICF</div>
        <div className="heading text-[15px] font-medium tracking-tight leading-tight">
            {ICF_EXCERPT.title}
        </div>
        <div className="text-[11px] text-neutral-500 mono mt-1">
            {ICF_EXCERPT.study_title}
        </div>
        <div className="text-[10px] mono uppercase tracking-wider text-neutral-500 mt-2">
            Version {ICF_EXCERPT.version}
        </div>
        <div className="mt-4 max-h-[440px] overflow-y-auto pr-2 border-t border-hair pt-4">
            {ICF_EXCERPT.sections.map((s) => (
                <div key={s.heading} className="mb-4 last:mb-0">
                    <div className="text-[12.5px] heading font-medium tracking-tight mb-1.5">
                        {s.heading}
                    </div>
                    <p className="text-[12px] text-neutral-700 leading-relaxed">
                        {s.body}
                    </p>
                </div>
            ))}
            <div className="text-[11px] text-neutral-400 italic border-t border-hair pt-3 mt-3">
                [ {ICF_EXCERPT.truncated_note} ]
            </div>
        </div>
    </div>
);

const RuleStatusPanel = () => (
    <div className="border border-hair bg-white" data-testid="tab-a-rules">
        <div className="px-4 py-3 border-b border-hair overline flex items-center justify-between">
            <span>Rule set · {RULE_STATUS.length} rules</span>
        </div>
        <div className="max-h-[440px] overflow-y-auto">
            {RULE_STATUS.map((r) => (
                <div
                    key={r.id}
                    className="px-4 py-2.5 border-b border-hair last:border-b-0 flex items-center gap-2"
                >
                    <div className="min-w-0 flex-1">
                        <div className="mono text-[10px] text-[#C2410C] truncate">
                            {r.citation}
                        </div>
                        <div className="text-[12px] heading tracking-tight truncate">
                            {r.title}
                        </div>
                    </div>
                    <StatusPill status={r.status} small />
                </div>
            ))}
        </div>
    </div>
);

const FindingCard = ({ finding, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div
            className="border border-hair bg-white"
            data-testid={`finding-${finding.id}`}
        >
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="w-full text-left px-4 py-3 hover:bg-neutral-50 transition-colors"
            >
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        {open ? (
                            <ChevronDown size={14} className="text-neutral-500 shrink-0" />
                        ) : (
                            <ChevronRight size={14} className="text-neutral-500 shrink-0" />
                        )}
                        <StatusPill status={finding.severity} small />
                        <span className="mono text-[10px] text-[#C2410C] shrink-0">
                            {finding.rule}
                        </span>
                    </div>
                </div>
                <div className="text-[13px] heading font-medium tracking-tight mt-2">
                    {finding.title}
                </div>
            </button>
            {open && (
                <div className="px-4 pb-4 border-t border-hair pt-3">
                    <p className="text-[12px] text-neutral-700 leading-relaxed">
                        {finding.description}
                    </p>
                    {finding.recommended_action && (
                        <div className="mt-3 border-l-2 border-[#C2410C] pl-3">
                            <div className="overline text-[9px] mb-1">
                                Recommended action
                            </div>
                            <p className="text-[12px] text-neutral-800 leading-relaxed">
                                {finding.recommended_action}
                            </p>
                        </div>
                    )}
                    <div className="mt-3 mono text-[11px] text-neutral-500">
                        Citation · {finding.citation}
                    </div>
                    {finding.historical_note && (
                        <div className="mt-2 text-[11px] italic text-[#9A3412]">
                            {finding.historical_note}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const FindingsPanel = () => (
    <div data-testid="tab-a-findings">
        <div className="overline mb-3">Compliance findings</div>
        <div className="grid grid-cols-4 border-l border-hair mb-4">
            {[
                { label: "Critical", n: FINDINGS_SUMMARY.critical, color: "#991B1B" },
                { label: "Gaps", n: FINDINGS_SUMMARY.gap, color: "#9A3412" },
                { label: "Advisory", n: FINDINGS_SUMMARY.advisory, color: "#854D0E" },
                { label: "Satisfied", n: FINDINGS_SUMMARY.satisfied, color: "#166534" },
            ].map((s) => (
                <div key={s.label} className="p-3 border-r border-b border-hair bg-white text-center">
                    <div
                        className="heading text-xl font-medium tracking-tight leading-none"
                        style={{ color: s.color }}
                    >
                        {s.n}
                    </div>
                    <div className="overline text-[9px] mt-2">{s.label}</div>
                </div>
            ))}
        </div>
        <div className="flex flex-col gap-3">
            {FINDINGS.map((f) => (
                <FindingCard
                    key={f.id}
                    finding={f}
                    defaultOpen={f.severity === "Critical Gap"}
                />
            ))}
        </div>
    </div>
);

// ---------- Tab B: Participant Impact ----------

const ParticipantModal = ({ pid, onClose }) => {
    if (pid !== "P-0003") {
        // Simple generic detail for other participants (all use same shape)
        const p = PARTICIPANTS.find((x) => x.id === pid);
        if (!p) return null;
        return (
            <div
                className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                onClick={onClose}
                data-testid="participant-modal"
            >
                <div
                    onClick={(e) => e.stopPropagation()}
                    className="bg-white border border-hair max-w-lg w-full p-6 rounded-sm max-h-[80vh] overflow-y-auto"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="overline mb-1">Participant detail</div>
                            <div className="heading text-xl font-medium tracking-tight">
                                {p.id} · {p.site}
                            </div>
                        </div>
                        <button onClick={onClose} data-testid="modal-close">
                            <X size={16} className="text-neutral-500" />
                        </button>
                    </div>
                    <dl className="mt-5 space-y-2 text-[12.5px]">
                        <div className="flex justify-between border-b border-hair pb-2">
                            <dt className="text-neutral-500">ICF version signed</dt>
                            <dd className="mono">{p.icf}</dd>
                        </div>
                        <div className="flex justify-between border-b border-hair pb-2">
                            <dt className="text-neutral-500">Signed on</dt>
                            <dd className="mono">{p.signed}</dd>
                        </div>
                        <div className="flex justify-between border-b border-hair pb-2">
                            <dt className="text-neutral-500">Decision</dt>
                            <dd className="font-medium">{p.decision}</dd>
                        </div>
                        <div className="flex justify-between border-b border-hair pb-2">
                            <dt className="text-neutral-500">Method</dt>
                            <dd>{p.method}</dd>
                        </div>
                        <div className="flex justify-between">
                            <dt className="text-neutral-500">Timing</dt>
                            <dd>{p.timing}</dd>
                        </div>
                    </dl>
                    <div className="mt-4 text-[12px] text-neutral-700 leading-relaxed border-l-2 border-[#C2410C] pl-3">
                        {p.reasoning}
                    </div>
                </div>
            </div>
        );
    }
    const d = P0003_DETAIL;
    return (
        <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="participant-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-hair max-w-2xl w-full p-6 rounded-sm max-h-[85vh] overflow-y-auto"
            >
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="overline mb-1">Participant detail · rule drilldown</div>
                        <div className="heading text-xl font-medium tracking-tight">
                            {d.id} · {d.site}
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="modal-close">
                        <X size={16} className="text-neutral-500" />
                    </button>
                </div>

                <div className="mt-6">
                    <div className="overline mb-2">Consent history</div>
                    <ul className="text-[12.5px] space-y-1.5">
                        {d.consent_history.map((c, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="mono text-[11px] text-[#C2410C] w-14 shrink-0">
                                    {c.version}
                                </span>
                                <span className="text-neutral-700">
                                    {c.signed
                                        ? `signed ${c.signed} · ${c.note}`
                                        : c.note}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-5">
                    <div className="overline mb-2">Visit history</div>
                    <ul className="text-[12.5px] space-y-1.5">
                        {d.visit_history.map((v, i) => (
                            <li key={i} className="flex gap-3">
                                <span className="mono text-[11px] text-neutral-500 w-28 shrink-0">
                                    {v.date}
                                </span>
                                <span className="text-neutral-700">
                                    {v.visit}
                                    {v.note && (
                                        <span className="text-neutral-500 italic ml-1">
                                            · {v.note}
                                        </span>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-5">
                    <div className="overline mb-2">Rule triggers</div>
                    <ul className="text-[12.5px] space-y-1.5">
                        {d.rule_triggers.map((r) => (
                            <li key={r.id} className="flex gap-3">
                                <span className="mono text-[10px] text-[#C2410C] shrink-0 w-40">
                                    {r.id}
                                </span>
                                <span className="text-neutral-700">
                                    {r.reason}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-5 border-l-2 border-[#FCA5A5] pl-3 bg-[#FEE2E2] p-3">
                    <div className="overline text-[9px] mb-1 text-[#991B1B]">
                        Additional flag
                    </div>
                    <p className="text-[12px] text-neutral-800 leading-relaxed">
                        {d.additional_flag}
                    </p>
                </div>

                <div className="mt-6 text-[11.5px] mono text-neutral-500">
                    Recommended communication template · draft available (non-functional in demo)
                </div>
            </div>
        </div>
    );
};

const ROWS_PER_PAGE = 15;

const ParticipantTable = ({ autoOpenPid }) => {
    const [siteFilter, setSiteFilter] = useState("All");
    const [decisionFilter, setDecisionFilter] = useState("All");
    const [sortBy, setSortBy] = useState("id");
    const [page, setPage] = useState(1);
    const [modalPid, setModalPid] = useState(null);

    // Story mode: auto-open the P-0003 modal 1.5s after Tab B mounts so
    // the narrator's line about "look at P-0003" has a moment to land.
    useEffect(() => {
        if (autoOpenPid) {
            const t = setTimeout(() => setModalPid(autoOpenPid), 1500);
            return () => clearTimeout(t);
        }
    }, [autoOpenPid]);

    const filtered = useMemo(() => {
        let rows = PARTICIPANTS.slice();
        if (siteFilter !== "All") rows = rows.filter((r) => r.site === siteFilter);
        if (decisionFilter !== "All")
            rows = rows.filter((r) => r.decision === decisionFilter);
        if (sortBy === "site") rows.sort((a, b) => a.site.localeCompare(b.site));
        else if (sortBy === "timing")
            rows.sort((a, b) => a.timing.localeCompare(b.timing));
        else rows.sort((a, b) => a.id.localeCompare(b.id));
        return rows;
    }, [siteFilter, decisionFilter, sortBy]);

    const pageCount = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
    const visibleRows = filtered.slice(
        (page - 1) * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE,
    );

    const siteOptions = [
        "All",
        ...Array.from({ length: 12 }, (_, i) => `Site ${String(i + 1).padStart(2, "0")}`),
    ];

    return (
        <div data-testid="participant-impact">
            <p className="text-[13px] text-neutral-700 leading-relaxed max-w-4xl mb-6">
                Amendment v2.2 requires a per-participant re-consent decision
                for all 87 enrolled participants across 12 sites. AudRI
                generates the decision, the reasoning, the method (full
                re-consent, addendum, notification), and the recommended
                timing.
            </p>

            {/* Site-level summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 border-l border-hair mb-6">
                {[
                    { label: "Enrolled", n: PARTICIPANT_SUMMARY.total, accent: false },
                    { label: "Full re-consent", n: PARTICIPANT_SUMMARY.full_reconsent, accent: true },
                    { label: "Addendum", n: PARTICIPANT_SUMMARY.addendum, accent: false },
                    { label: "Notification", n: PARTICIPANT_SUMMARY.notification, accent: false },
                    { label: "Not applicable", n: PARTICIPANT_SUMMARY.not_applicable, accent: false },
                ].map((s) => (
                    <div
                        key={s.label}
                        className="p-6 border-r border-b border-hair bg-white"
                    >
                        <div className="overline text-[9px] mb-2">{s.label}</div>
                        <div
                            className={`heading text-3xl font-medium tracking-tighter leading-none ${
                                s.accent ? "text-[#C2410C]" : ""
                            }`}
                        >
                            {s.n}
                        </div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3 mb-4 flex-wrap">
                <div className="overline flex items-center gap-2 mr-2">
                    <Filter size={11} /> Filter
                </div>
                <select
                    value={siteFilter}
                    onChange={(e) => {
                        setSiteFilter(e.target.value);
                        setPage(1);
                    }}
                    data-testid="filter-site"
                    className="border border-hair bg-white px-3 py-1.5 rounded-sm text-[12px] focus:outline-none focus:border-[#C2410C]"
                >
                    {siteOptions.map((s) => (
                        <option key={s}>{s}</option>
                    ))}
                </select>
                <select
                    value={decisionFilter}
                    onChange={(e) => {
                        setDecisionFilter(e.target.value);
                        setPage(1);
                    }}
                    data-testid="filter-decision"
                    className="border border-hair bg-white px-3 py-1.5 rounded-sm text-[12px] focus:outline-none focus:border-[#C2410C]"
                >
                    <option>All</option>
                    <option>Full re-consent</option>
                    <option>Addendum</option>
                    <option>Notification only</option>
                </select>
                <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    data-testid="filter-sort"
                    className="border border-hair bg-white px-3 py-1.5 rounded-sm text-[12px] focus:outline-none focus:border-[#C2410C]"
                >
                    <option value="id">Sort · Participant ID</option>
                    <option value="site">Sort · Site</option>
                    <option value="timing">Sort · Timing</option>
                </select>
                <div className="ml-auto text-[11px] mono text-neutral-500">
                    {filtered.length} matching · page {page} of {pageCount}
                </div>
            </div>

            {/* Table */}
            <div className="border border-hair bg-white overflow-x-auto">
                <div className="min-w-[1100px]">
                    <div className="grid grid-cols-[100px_90px_60px_100px_140px_1fr_120px_140px_100px] px-4 py-3 border-b border-hair overline text-[10px] gap-3">
                        <div>Participant</div>
                        <div>Site</div>
                        <div>ICF</div>
                        <div>Signed</div>
                        <div>Decision</div>
                        <div>Reasoning</div>
                        <div>Method</div>
                        <div>Timing</div>
                        <div></div>
                    </div>
                    {visibleRows.map((r) => (
                        <div
                            key={r.id}
                            data-testid={`row-${r.id}`}
                            className="grid grid-cols-[100px_90px_60px_100px_140px_1fr_120px_140px_100px] px-4 py-3 border-b border-hair last:border-b-0 items-center gap-3 hover:bg-neutral-50"
                        >
                            <div className="mono text-[11px] text-[#C2410C]">
                                {r.id}
                            </div>
                            <div className="mono text-[11px]">{r.site}</div>
                            <div className="mono text-[11px]">{r.icf}</div>
                            <div className="mono text-[11px] text-neutral-500">
                                {r.signed}
                            </div>
                            <div>
                                <span
                                    className={`mono text-[10px] uppercase tracking-wider px-2 py-[2px] border rounded-sm ${
                                        r.decision === "Full re-consent"
                                            ? "text-[#991B1B] bg-[#FEE2E2] border-[#FCA5A5]"
                                            : r.decision === "Addendum"
                                              ? "text-[#9A3412] bg-[#FFEDD5] border-[#FED7AA]"
                                              : "text-neutral-600 bg-neutral-100 border-hair"
                                    }`}
                                >
                                    {r.decision}
                                </span>
                            </div>
                            <div className="text-[12px] text-neutral-700 truncate" title={r.reasoning}>
                                {r.reasoning}
                            </div>
                            <div className="text-[12px] text-neutral-700">
                                {r.method}
                            </div>
                            <div className="text-[12px] text-neutral-700">
                                {r.timing}
                            </div>
                            <div>
                                <button
                                    type="button"
                                    onClick={() => setModalPid(r.id)}
                                    data-testid={`btn-details-${r.id}`}
                                    className="text-[11px] mono uppercase tracking-wider text-[#C2410C] hover:underline"
                                >
                                    View →
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-end gap-2 mt-4">
                <button
                    type="button"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="inline-flex items-center gap-1 border border-hair px-2.5 py-1.5 rounded-sm text-[11px] disabled:opacity-40 hover:bg-white"
                >
                    <ChevronLeft size={12} /> Prev
                </button>
                <button
                    type="button"
                    onClick={() => setPage(Math.min(pageCount, page + 1))}
                    disabled={page === pageCount}
                    className="inline-flex items-center gap-1 border border-hair px-2.5 py-1.5 rounded-sm text-[11px] disabled:opacity-40 hover:bg-white"
                >
                    Next <ChR size={12} />
                </button>
            </div>

            {modalPid && (
                <ParticipantModal
                    pid={modalPid}
                    onClose={() => setModalPid(null)}
                />
            )}
        </div>
    );
};

// ---------- Page ----------

export default function IcfExecute() {
    const [searchParams] = useSearchParams();
    const storyMode = searchParams.get("story") === "1";
    const storyTab = searchParams.get("tab"); // "A" or "B" when in story mode
    const [tab, setTab] = useState(storyTab === "B" ? "B" : "A");

    // Keep tab state in sync with URL when the story navigates between Tab A / Tab B
    useEffect(() => {
        if (storyMode && storyTab && storyTab !== tab) {
            setTab(storyTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storyMode, storyTab]);

    const narratorConfig = storyMode
        ? tab === "A"
            ? {
                  slide: 5,
                  label: "2a · Execute — the document",
                  body: "Two Critical Gaps: biomarker procedures not disclosed, biospecimen future use not disclosed. Two Gaps: venipuncture risks missing, alternative procedures thin. Each finding comes with the citation and the exact remediation your medical writer needs — no round-trip through sponsor QA to figure out what to fix.",
                  ctaLabel: "Show me who's affected",
                  nextTo: "/icf/execute?story=1&tab=B",
              }
            : {
                  slide: 6,
                  label: "2b · Execute — the population",
                  body: "All 87 participants triaged. Look at P-0003 — signed on the prior ICF v3.0, two versions behind. Prior-version compliance gap surfaced automatically. That's the kind of finding that lives in an SDV report six months from now. AudRI put it on your screen today.",
                  ctaLabel: "Show me what the inspector sees",
                  nextTo: "/icf/explain?story=1",
              }
        : null;

    return (
        <IcfLayout>
            <ScreenHeader
                title="Execute — Compliance Comparison + Participant Impact"
                subtitle="AudRI compares the extracted rule set against the current ICF v3.1 and against each participant's consent history. It surfaces compliance gaps and re-consent decisions."
            />

            <div className="flex gap-1 border-b border-hair mb-6">
                {[
                    { k: "A", label: "Document Compliance", testid: "tab-a" },
                    { k: "B", label: "Participant Impact", testid: "tab-b" },
                ].map((t) => (
                    <button
                        key={t.k}
                        type="button"
                        onClick={() => setTab(t.k)}
                        data-testid={t.testid}
                        className={`px-5 py-3 text-[13px] heading font-medium tracking-tight border-b-2 transition-colors ${
                            tab === t.k
                                ? "border-[#C2410C] text-[#0A0A0A]"
                                : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "A" && (
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,340px)_minmax(0,1fr)] gap-6">
                    <IcfExcerptPanel />
                    <RuleStatusPanel />
                    <FindingsPanel />
                </div>
            )}
            {tab === "B" && (
                <ParticipantTable
                    autoOpenPid={storyMode && storyTab === "B" ? "P-0003" : null}
                />
            )}

            {/* Extra bottom padding when narrator bar is present so its
                fixed positioning doesn't cover the last row. */}
            {storyMode && <div className="h-32" />}

            {!storyMode && (
                <StepCta
                    to="/icf/explain"
                    label="Continue to Explain"
                    testid="cta-continue-explain"
                />
            )}
            {storyMode && narratorConfig && (
                <StoryNarratorBar
                    slide={narratorConfig.slide}
                    label={narratorConfig.label}
                    body={narratorConfig.body}
                    ctaLabel={narratorConfig.ctaLabel}
                    nextTo={narratorConfig.nextTo}
                />
            )}
        </IcfLayout>
    );
}
